import {
  Avatar,
  Col,
  Row,
  Typography,
  message,
  Switch,
  Card,
  Spin,
  Descriptions,
  Button,
  Input,
} from "antd"
import React from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import {
  NotificationOutlined,
  BellOutlined,
  CheckOutlined,
  InboxOutlined,
  PhoneOutlined,
} from "@ant-design/icons"
import { useCollection } from "../../../hooks/useCollection"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"

const { Text } = Typography

const styles = {
  spinContainer: {
    height: "calc(100vh - 200px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  pageContainer: {
    padding: 24,
  },
  userHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: "1px solid #f0f0f0",
  },
  userInfo: {
    overflow: "hidden",
  },
  userName: {
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userEmail: {
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: 12,
  },
  settingLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    paddingBottom: 4,
  },
  settingIcon: {
    color: "#8c8c8c",
  },
  settingContent: {
    display: "flex",
    justifyContent: "flex-end",
    paddingBottom: 4,
  },
}

export default function UserNotifications() {
  const { loading, data } = useCollection("users")
  const specialUserId = "9QZwmym1PbSVReUaQ4eVLhji6ww1"

  const updateUserField = async (userId, field, value) => {
    const userRef = doc(db, "users", userId)
    try {
      await updateDoc(userRef, { [field]: value })
      message.success("Setting updated")
    } catch (err) {
      console.error("Error updating field:", err)
      message.error("Failed to update setting")
    }
  }

  const resetUserAppLogin = async (userId) => {
    await updateUserField(userId, "hasAppLogin", false)
  }

  const settings = [
    {
      key: "sendDailyOrderRecapMobileNotification",
      title: "Daily Recap",
      icon: <InboxOutlined />,
    },
    {
      key: "sendOrderCompletedMobileNotification",
      title: "Order Complete",
      icon: <CheckOutlined />,
    },
    {
      key: "sendOrderDoneByDesignerMobileNotification",
      title: "Design Approval",
      icon: <BellOutlined />,
    },
  ]

  const renderUserCard = (user) => (
    <Card size="small" style={{ borderRadius: 8 }}>
      <div style={styles.userHeader}>
        <Avatar size={40} src={user.imageUrl || "https://via.placeholder.com/40"} />
        <div style={styles.userInfo}>
          <Text strong style={styles.userName}>
            {user.name}
          </Text>
          <Text type="secondary" style={styles.userEmail}>
            {user.email}
          </Text>
        </div>
      </div>

      <Descriptions
        size="small"
        column={1}
        colon={false}
        labelStyle={styles.settingLabel}
        contentStyle={styles.settingContent}>
        {settings.map((setting) => (
          <Descriptions.Item
            key={setting.key}
            label={
              <span style={styles.settingLabel}>
                <span style={styles.settingIcon}>{setting.icon}</span>
                <Text>{setting.title}</Text>
              </span>
            }>
            <Switch
              size="small"
              checked={user[setting.key] || false}
              onChange={(checked) => updateUserField(user.id, setting.key, checked)}
              disabled={loading}
            />
          </Descriptions.Item>
        ))}

        {/* Special user extra fields */}
        {user.id === specialUserId && (
          <>
            <Descriptions.Item
              label={
                <span style={styles.settingLabel}>
                  <PhoneOutlined style={styles.settingIcon} />
                  <Text>SMS Notifications</Text>
                </span>
              }>
              <Switch
                size="small"
                checked={user.smsNotif || false}
                onChange={(checked) => updateUserField(user.id, "smsNotif", checked)}
                disabled={loading}
              />
            </Descriptions.Item>

            <Descriptions.Item
              label={
                <span style={styles.settingLabel}>
                  <PhoneOutlined style={styles.settingIcon} />
                  <Text>Phone Number</Text>
                </span>
              }>
              <Input
                size="small"
                style={{ width: "100%" }}
                defaultValue={user.phoneNumber || ""}
                onBlur={(e) => updateUserField(user.id, "phoneNumber", e.target.value)}
                placeholder="+37120000000"
              />
            </Descriptions.Item>
          </>
        )}

        <Descriptions.Item label="Reset user app login.">
          <Button
            disabled={!user.hasAppLogin}
            size="small"
            onClick={() => resetUserAppLogin(user.id)}>
            Reset
          </Button>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )

  if (loading) {
    return (
      <div style={styles.spinContainer}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={styles.pageContainer}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <PageHeader text="Notification Settings" icon={<NotificationOutlined />} />
        </Col>
        {data.map((user) => (
          <Col xs={24} sm={12} lg={6} key={user.id}>
            {renderUserCard(user)}
          </Col>
        ))}
      </Row>
    </div>
  )
}
